import { useEffect, useRef } from 'react'
import { Dialogs, useDialogs } from '../../sharedComponents/src/Contexts/DialogsContext'
import { useHeaderButtons } from '../../sharedComponents/src/Contexts/HeaderButtonsContext'
import { BonusType, DailyBonus } from '../../sharedComponents/src/DailyBonus/bonusTypes'
import DailyBonusButton from '../../sharedComponents/src/DailyBonus/DailyBonusButton'
import DailyBonusDialog from '../../sharedComponents/src/DailyBonus/DailyBonusDialog'
import { analyticsManager } from '../../sharedComponents/src/globalHeader/services/analytics/AnalyticsManager'
import useFullscreenAnimation from '../../sharedComponents/src/hooks/useFullscreenAnimation'
import { useStoreActions, useStoreState } from '../../store/hooks'

// A failed fetch keeps the last bonus; retry so the chest recovers after a wake-up or a wallet outage
const BONUS_RETRY_DELAYS = [30_000, 120_000, 300_000]

export const DailyBonusService = () => {
    // States
    const userData = useStoreState((state) => state.userData)
    const bonuses = useStoreState((state) => state.dailyBonus.bonuses)
    const initialHintAmounts = useStoreState((state) => state.dailyBonus.initialHintAmounts)

    // Actions
    const getBonus = useStoreActions((state) => state.dailyBonus.getBonus)
    const getAdditionalBonusInfo = useStoreActions((state) => state.dailyBonus.getAdditionalBonusInfo)
    const claimBonus = useStoreActions((state) => state.dailyBonus.claimBonus)
    const setHintsAmount = useStoreActions((state) => state.dailyBonus.setHintsAmount)
    const setSnackbarMessage = useStoreActions((state) => state.setSnackbarMessage)

    // Hooks
    const { registerButton, unregisterButton } = useHeaderButtons()
    const { registerDialog, unregisterDialog, dialogRegistry } = useDialogs()
    const { showAnimation } = useFullscreenAnimation()
    const retryTimeout = useRef<NodeJS.Timeout>()
    const backoffTimeout = useRef<NodeJS.Timeout>()
    const lastFetchFailed = useRef(false)
    const lastVisibilityFetchAt = useRef(0)

    const fetchBonus = async (attempt = 0) => {
        const result = await getBonus(BonusType.DAILY_BONUS_HINT)
        lastFetchFailed.current = result === 'transient'
        clearTimeout(backoffTimeout.current)
        // a 4xx won't change by retrying; the next countdown or a reload tries again
        if (result === 'transient' && attempt < BONUS_RETRY_DELAYS.length) {
            // a hidden page is refetched by the visibilitychange handler instead
            backoffTimeout.current = setTimeout(
                () => !document.hidden && fetchBonus(attempt + 1),
                BONUS_RETRY_DELAYS[attempt],
            )
        }
    }

    useEffect(() => () => clearTimeout(backoffTimeout.current), [])

    // Fetch daily bonuses
    useEffect(() => {
        if (userData.userData?.id) {
            fetchBonus()
            getAdditionalBonusInfo(BonusType.INITIAL_BONUS_HINT)
            setHintsAmount(-1)
        }
    }, [userData.userData?.id, userData.userData?.user_name])

    // Claim bonus handler
    const claimBonusHandler = async (bonusType: BonusType, amount: number) => {
        if (!(await claimBonus(bonusType))) {
            setSnackbarMessage("Couldn't collect the bonus. Please try again.")
            // a claim without a response may still have been granted: resync the chest
            fetchBonus()
            return
        }
        showAnimation('/animations/daily-bonus/chest-animation-v3.lottie')
        analyticsManager.dispatchEvent('dailyBonusCollected', {
            pageUrl: window.location.href,
            rewardType: 'hints',
            rewardAmount: amount,
        })
    }

    // Open bonus dialog
    const onButtonClick = async (bonus: DailyBonus) => {
        if (userData.userData?.groups) {
            registerDialog(
                Dialogs.DAILY_BONUS,
                <DailyBonusDialog bonus={bonus} claimBonus={claimBonusHandler} additionalData={initialHintAmounts} />,
            )
            analyticsManager.dispatchEvent('dailyBonusClicked', {
                pageUrl: window.location.href,
            })
        }
    }

    // Handle daily bonus logic
    const handleDailyBonus = () => {
        Object.keys(bonuses).forEach((key) => {
            if (key === BonusType.DAILY_BONUS_HINT) {
                const bonus = bonuses[key]
                if (bonus) {
                    // Set the retry timeout based on availableIn time
                    const availableDate = new Date(bonus.availableIn)
                    const delay = Math.max(availableDate.getTime() - Date.now(), 0)
                    retryTimeout.current = setTimeout(() => {
                        fetchBonus()
                    }, delay)

                    if (dialogRegistry.dailyBonus) {
                        // Update dialog if it's already open
                        unregisterDialog(Dialogs.DAILY_BONUS)
                        registerDialog(
                            Dialogs.DAILY_BONUS,
                            <DailyBonusDialog
                                bonus={bonus}
                                claimBonus={claimBonusHandler}
                                additionalData={initialHintAmounts}
                            />,
                        )
                    }

                    // Register daily bonus button
                    registerButton(
                        'dailyBonus',
                        <DailyBonusButton isActive={bonus.ifEligible} onClick={() => onButtonClick(bonus)} />,
                    )
                } else {
                    // Unregister daily bonus button and clear timers
                    unregisterButton('dailyBonus')
                    if (retryTimeout.current) {
                        clearTimeout(retryTimeout.current)
                    }
                }
            }
        })
    }

    // Update bonuses
    useEffect(() => {
        handleDailyBonus()

        // Cleanup on unmount or when bonuses change
        return () => {
            unregisterButton('dailyBonus')
            if (retryTimeout.current) {
                clearTimeout(retryTimeout.current)
            }
        }
    }, [bonuses])

    // Back from sleep/background: refetch if the last fetch failed or the countdown ran out meanwhile
    useEffect(() => {
        const onVisibilityChange = () => {
            const bonus = bonuses[BonusType.DAILY_BONUS_HINT]
            const overdue = bonus && !bonus.ifEligible && new Date(bonus.availableIn).getTime() <= Date.now()
            const throttled = Date.now() - lastVisibilityFetchAt.current < 60_000
            if (document.visibilityState === 'visible' && (lastFetchFailed.current || overdue) && !throttled) {
                lastVisibilityFetchAt.current = Date.now()
                fetchBonus()
            }
        }
        document.addEventListener('visibilitychange', onVisibilityChange)
        return () => document.removeEventListener('visibilitychange', onVisibilityChange)
    }, [bonuses])

    return null
}
